import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, User as UserIcon } from "lucide-react";

export default function RolePlayChat() {
  const { id } = useParams();
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [score, setScore] = useState(null);
  const [scoring, setScoring] = useState(false);

  const sessionId = useMemo(
    () => `${id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [id]
  );
  const bottomRef = useRef(null);

  useEffect(() => { api.get(`/scenarios/${id}`).then(r => setScenario(r.data)); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userMsg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/roleplay/chat", { scenario_id: id, session_id: sessionId, message: userMsg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error("Chat error — try again");
    } finally { setBusy(false); }
  };

  const finish = async () => {
    if (messages.length < 2) { toast("Have at least one exchange first"); return; }
    setScoring(true);
    try {
      const { data } = await api.post("/roleplay/score", { scenario_id: id, session_id: sessionId });
      setScore(data);
    } catch { toast.error("Scoring failed"); }
    finally { setScoring(false); }
  };

  if (!scenario) return <div className="text-sm text-neutral-500">Loading…</div>;

  return (
    <div className="grid lg:grid-cols-12 gap-8 min-h-[calc(100vh-14rem)]">
      <aside className="lg:col-span-4">
        <Link to="/scenarios" className="label-eyebrow" data-testid="back-scenarios">← All role-plays</Link>
        <div className="mt-4 label-eyebrow text-[var(--brand-primary)]">{scenario.difficulty}</div>
        <h1 className="font-display text-3xl md:text-4xl leading-tight mt-2">{scenario.title}</h1>
        <p className="text-sm text-neutral-500 mt-3">{scenario.description}</p>

        <div className="mt-8 border hairline p-5 bg-[color:var(--brand-surface2)]">
          <div className="label-eyebrow mb-3">Your prospect</div>
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{scenario.persona}</p>
        </div>

        <button onClick={finish} disabled={scoring || messages.length<2} className="btn-primary w-full mt-6" data-testid="finish-and-score">
          {scoring ? "Coaching…" : "Finish & get coaching"}
        </button>

        {score && (
          <div className="mt-6 border hairline p-5 space-y-3" data-testid="score-panel">
            <div className="flex items-baseline justify-between">
              <div className="label-eyebrow">Overall</div>
              <div className="font-display text-4xl">{score.overall}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {["rapport","objection_handling","discovery","closing"].map(k => (
                <div key={k}>
                  <div className="label-eyebrow" style={{fontSize:'0.6rem'}}>{k.replace("_"," ")}</div>
                  <div className="font-mono">{score[k] ?? "—"}</div>
                </div>
              ))}
            </div>
            {score.strengths?.length > 0 && (
              <div>
                <div className="label-eyebrow mt-3">Strengths</div>
                <ul className="mt-1 text-sm list-disc pl-4">{score.strengths.map((s,i)=><li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {score.improvements?.length > 0 && (
              <div>
                <div className="label-eyebrow mt-3">Improve</div>
                <ul className="mt-1 text-sm list-disc pl-4">{score.improvements.map((s,i)=><li key={i}>{s}</li>)}</ul>
              </div>
            )}
            {score.coach_note && (
              <div>
                <div className="label-eyebrow mt-3">Coach's note</div>
                <p className="text-sm italic mt-1">{score.coach_note}</p>
              </div>
            )}
          </div>
        )}
      </aside>

      <section className="lg:col-span-8 flex flex-col border hairline bg-white min-h-[70vh]">
        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-neutral-400 text-sm">
              Say hello and open the conversation. Your prospect is waiting&hellip;
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role !== "user" && (
                <div className="w-8 h-8 border hairline flex items-center justify-center flex-shrink-0">
                  <Sparkles size={14} className="text-[var(--brand-primary)]"/>
                </div>
              )}
              <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[color:var(--brand-primary)] text-white"
                  : "bg-[color:var(--brand-surface2)] text-[var(--brand-primary)]"
              }`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 border hairline flex items-center justify-center flex-shrink-0">
                  <UserIcon size={14}/>
                </div>
              )}
            </div>
          ))}
          {busy && <div className="text-xs text-neutral-400 pl-11">Prospect is typing…</div>}
          <div ref={bottomRef}/>
        </div>

        <div className="border-t hairline p-4 flex items-end gap-2">
          <textarea
            rows={2}
            value={input}
            onChange={(e)=>setInput(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==="Enter" && !e.shiftKey){e.preventDefault(); send();}}}
            placeholder="Your response…"
            data-testid="chat-input"
            className="flex-1 bg-transparent border hairline px-3 py-2 resize-none focus:outline-none focus:border-[color:var(--brand-primary)]"
          />
          <button onClick={send} disabled={busy || !input.trim()} className="btn-primary flex items-center gap-2" data-testid="chat-send">
            <Send size={14}/> Send
          </button>
        </div>
      </section>
    </div>
  );
}
